import React, { useState, useEffect, useCallback } from "react";
import Pdf from "../../../../../../Components/Icons/Pdf";
import Xls from "../../../../../../Components/Icons/Xls";
import NewReportTable from "../../../../../../Components/Others/NewReportTable";
import {
  CancelButton,
  SubmitButton,
} from "../../../../../../Components/Utils/Buttons";
import { useFormik } from "formik";
import UseFetchReportData from "../../../../../../Components/Hooks/useFetchReportData";
import axiosInstance from "../../../../../../Services/axiosCommon";
import { FormikSelectTwoSmall } from "../../../../../../Components/Utils/Select";
import { NuDateSmall } from "../../../../../../Components/Utils/Input";
import UseDropDown from "../../../../../../Components/Hooks/useDropDown";
import useFetchMDIndex from "../../../../../../Components/Hooks/useFetchMDIndex";
import NuDateFormat from "../../../../../../Components/Utils/NuDate";
import Pagination from "../../../../../../Components/Utils/Pagenation";
import { useToast } from "../../../../../../Components/Others/ToastServices";

function CustomerQuoteReport() {
  const toast = useToast();
  const [isDownloading, setIsDownloading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(
    Number(localStorage.getItem("perPageView")) || 20,
  );

  const { data: Salesman } = UseDropDown("/user/salesman/drop-down");
  const { value: status } = useFetchMDIndex(
    `/dropdown/dropdown-details/`,
    "Status_customerQuotes",
    true,
  );

  const [params, setParams] = useState({
    page: 1,
    per_page: pageCount,
    status: "",
    dateFrom: "",
    dateTo: "",
    salesPerson: "",
  });

  // ✅ Sync pagination safely
  useEffect(() => {
    setParams((prev) => ({
      ...prev,
      page,
      per_page: pageCount,
    }));
  }, [page, pageCount]);

  const formik = useFormik({
    initialValues: {
      status: "",
      dateFrom: "",
      dateTo: "",
      salesPerson: "",
    },
    onSubmit: (values) => {
      setPage(1);
      setParams((prev) => ({
        ...prev,
        page: 1,
        ...values,
      }));
    },
  });

  const { data, dataLoading, total } = UseFetchReportData("/report/cq", params);

  // ✅ Clear filters
  const clearFilters = () => {
    formik.resetForm();
    setPage(1);
    setParams({
      page: 1,
      per_page: pageCount,
      status: "",
      dateFrom: "",
      dateTo: "",
      salesPerson: "",
    });
  };

  // ✅ Reusable download function
  const handleDownload = useCallback(
    async (type) => {
      setIsDownloading(true);
      try {
        const { page, per_page, ...downloadParams } = params;

        const response = await axiosInstance.get(`/report/cq/${type}`, {
          params: downloadParams,
          responseType: "blob",
        });

        const blob = new Blob([response.data], {
          type:
            type === "pdf"
              ? "application/pdf"
              : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `Customer Quote Report ${NuDateFormat({
          value: new Date(),
          format: "DD-MM-YYYY",
        })}.${type}`;

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url); // ✅ cleanup
        toast.open({
          type: "success",
          message: "Success",
          description: `${type.toUpperCase()} Downloaded Successfully`,
        });
      } catch (error) {
        console.error(`Error downloading ${type}:`, error);
        toast.open({
          type: "error",
          message: "Error",
          description: `Error downloading the ${type.toUpperCase()} file`,
        });
      } finally {
        setIsDownloading(false);
      }
    },
    [params, toast],
  );

  const headers = [
    {
      title: "YPR",
      width: "10%",
      field: "ypr",
      // textAlign: "left",
      // rowAlign: "left",
    },
    {
      title: "YPR Date",
      width: "10%",
      field: "yprDate",
      type: "date",
      // textAlign: "left",
      // rowAlign: "left",
    },
    {
      title: "YPR Desc.",
      width: "15%",
      field: "yprDesc",
      // textAlign: "left",
      // rowAlign: "left",
    },
    {
      title: "Quote No.",
      width: "10%",
      field: "quoteNo",
      // textAlign: "left",
      // rowAlign: "left",
    },
    {
      title: "Date",
      width: "10%",
      field: "date",
      type: "date",
      // textAlign: "left",
      // rowAlign: "left",
    },
    {
      title: "Amount",
      width: "10%",
      field: "amount",
      type: "amount",
      // textAlign: "left",
      // rowAlign: "left",
    },
    {
      title: "Customer",
      width: "15%",
      field: "customer",
      // textAlign: "left",
      // rowAlign: "left",
    },
    {
      title: "Sales Person",
      width: "10%",
      field: "salesperson",
      // textAlign: "left",
      // rowAlign: "left",
    },
    {
      title: "Status",
      width: "10%",
      field: "status",
      // textAlign: "left",
      // rowAlign: "left",
    },
  ];

  const downloadPDF = async () => {
    try {
      const { page, per_page, ...downloadParams } = params;
      const response = await axiosInstance.get(`/report/cq/pdf`, {
        params: downloadParams,
        responseType: "blob",
      });
      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(pdfBlob);

      link.download = `Customer Quote Report ${NuDateFormat({ value: new Date(), format: "DD-MM-YYYY" })}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("Error downloading the PDF:", error);
    }
  };

  const downloadxlsx = async () => {
    try {
      const { page, per_page, ...downloadParams } = params;
      const response = await axiosInstance.get(`/report/cq/xlsx`, {
        params: downloadParams,
        responseType: "blob",
      });
      const xlsxBlob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(xlsxBlob);

      link.download = `Customer Quote Report ${NuDateFormat({ value: new Date(), format: "DD-MM-YYYY" })}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading the xlsx:", error);
    }
  };

  return (
    <div className="w-full h-[calc(100vh-140px)] flex flex-col">
      {/* Header */}
      <div className="w-full bg-white flex-shrink-0">
        <div className="flex justify-between items-center">
          <p className="font-semibold text-xs 2xl:text-sm pt-1">
            Customer Quote : {total || 0}
          </p>

          <div className="flex gap-4 mt-1">
            <button
              onClick={() => handleDownload("pdf")}
              // onClick={downloadPDF}
              disabled={isDownloading}
              className={isDownloading ? "opacity-50 cursor-not-allowed" : ""}
            >
              <Pdf />
            </button>
            <button
              onClick={() => handleDownload("xlsx")}
              // onClick={downloadxlsx}
              disabled={isDownloading}
              className={isDownloading ? "opacity-50 cursor-not-allowed" : ""}
            >
              <Xls />
            </button>
          </div>
        </div>

        {/* Filters */}
        <form onSubmit={formik.handleSubmit}>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
            <NuDateSmall label="From Date" formik={formik} name="dateFrom" />
            <NuDateSmall label="To Date" formik={formik} name="dateTo" />
            <FormikSelectTwoSmall
              label="Status"
              Options={status}
              formik={formik}
              name="status"
              isClear
            />
            <FormikSelectTwoSmall
              label="Sales Person"
              Options={Salesman}
              formik={formik}
              name="salesPerson"
              isClear
            />

            <div className="col-span-1 sm:col-span-2 md:col-span-4 flex justify-end gap-2">
              <SubmitButton name="Apply" />
              <CancelButton label="Clear" handleClick={clearFilters} />
            </div>
          </div>
        </form>
      </div>

      {/* Table */}
      <div className="flex-grow overflow-y-auto">
        <NewReportTable
          headers={headers}
          data={data}
          dataLoading={dataLoading}
        />
      </div>

      {/* Pagination */}
      <div className="h-[40px] bg-white border-t">
        <Pagination
          total={total}
          pageIndex={page}
          setPageIndex={setPage}
          dataCount={pageCount}
          setDataCount={setPageCount}
        />
      </div>
    </div>
  );
}

export default CustomerQuoteReport;
